// File: frontend/src/context/BrandingContext.js
// Version: 1.1 (Phase 9 — features из /branding, аддитивно)
// Description: Single source of truth for tenant identity on the frontend.
// Replaces 3 duplicate fetch('/api/v1/branding') calls (Login.js:19, Home.js:37, Navbar.js:89).
// Owns the html.fitenko class toggle and conditional Fontsource CSS loading.

import React, { createContext, useContext, useEffect, useState } from 'react';

const DEFAULT_BRANDING = { instance_name: 'mlg', company_name: 'Legal OS' };

// Phase 9: признаки арендатора из /api/v1/branding (`features`). Нет поля / не объект → ПУСТО
// (у Fitenko/MLG до и после выкатки 09-02 — `{}`). Замороженный синглтон: ссылка стабильна
// для зависимостей эффектов.
const PUSTO_PRIZNAKOV = Object.freeze({});

const BrandingContext = createContext({
    branding: DEFAULT_BRANDING,
    isFitenko: false,
    loading: true,
    features: PUSTO_PRIZNAKOV,
    featuresError: null,
});

export const useBranding = () => useContext(BrandingContext);

// Module-scoped cache mirrors Navbar.js:11 `_brandingCache` pattern.
let _cache = null;

export const BrandingProvider = ({ children }) => {
    const [branding, setBranding] = useState(_cache || DEFAULT_BRANDING);
    const [loading, setLoading] = useState(!_cache);

    useEffect(() => {
        if (_cache) return;
        fetch('/api/v1/branding')
            .then((r) => r.json())
            .then((data) => {
                _cache = data;
                setBranding(data);
                // Persist for next-load FOUC prevention (inline <script> in index.html reads this).
                try { localStorage.setItem('lastTenant', data.instance_name); } catch (e) { /* localStorage disabled */ }
            })
            .catch(() => { /* network/parse error — keep default */ })
            .finally(() => setLoading(false));
    }, []);

    const isFitenko = branding.instance_name === 'fitenko';
    const features = (branding && branding.features && typeof branding.features === 'object' && !Array.isArray(branding.features)) ? branding.features : PUSTO_PRIZNAKOV;
    const featuresError = (branding && branding.features_error) || null;

    // Idempotent class toggle (inline <script> in index.html may have already set it).
    useEffect(() => {
        const html = document.documentElement;
        if (isFitenko) html.classList.add('fitenko');
        else html.classList.remove('fitenko');
    }, [isFitenko]);

    // Conditional font CSS — Webpack code-splits these into their own chunk.
    // Гарнитуры §3.1 макета: Manrope (интерфейс) и DM Serif Display (заголовки).
    // MLG bundle truly does not contain these .woff2 — импорт живёт под рубежом.
    useEffect(() => {
        if (!isFitenko) return;
        Promise.all([
            import('@fontsource/manrope/400.css'),
            import('@fontsource/manrope/500.css'),
            import('@fontsource/manrope/600.css'),
            import('@fontsource/manrope/700.css'),
            import('@fontsource/dm-serif-display/400.css'),
        ]).catch(() => { /* font load failure non-fatal — system fallback used */ });
    }, [isFitenko]);

    return (
        <BrandingContext.Provider value={{ branding, isFitenko, loading, features, featuresError }}>
            {children}
        </BrandingContext.Provider>
    );
};
